import { useTranslation } from 'next-i18next';
import { useContext, useEffect, useMemo } from 'react';

import { useFertilizationProcess } from '@/modules/periodicJobs/apihooks';
import {
  getDynamicFertilizersColumns,
  jobInstancesTableColumns,
} from '@/modules/periodicJobs/components/FertilizationTable/logic';
import { PeriodicJobsContext } from '@/modules/periodicJobs/context/context';
import { periodicJobsActionsTypes as ActionsTypes } from '@/modules/periodicJobs/context/reducer';
import { IPeriodicJobs } from '@/modules/periodicJobs/types';
import { Loading, Table } from '@/shared/components/atoms';

const PER_PAGE = 5;

const FertilizationTable: React.FC<FertilizationTableProps> = ({
  entityId,
  setFertilizationCount,
}) => {
  const {
    state: { startDate, endDate, selectedPlots, selectedCrop, tablePageNumber },
    dispatch,
  } = useContext(PeriodicJobsContext);

  const { t, i18n } = useTranslation('common');

  const { language } = i18n;

  const onPageChange = (page: number) => {
    dispatch({ type: ActionsTypes.setPageNumber, payload: page });
  };

  const { data, isLoading } = useFertilizationProcess({
    entityId,
    cropId: selectedCrop?.id,
    fromDate: startDate?.toISOString(),
    toDate: endDate?.toISOString(),
    plotIds: selectedPlots.map((p) => p.id),
    pageSize: PER_PAGE,
    pageNumber: tablePageNumber,
  });

  const jobInstancesData = data?.jobInstances;

  const [fertilizersData, isDivided] = useMemo(() => {
    const fData = getDynamicFertilizersColumns(jobInstancesData);
    const isDivided = fData.length > 0;
    return [fData, isDivided];
  }, [jobInstancesData]);

  useEffect(() => {
    if (setFertilizationCount && data) {
      setFertilizationCount(data.total);
    }
  }, [data, setFertilizationCount]);

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <Table<IPeriodicJobs.JobInstance>
          columnsDividerIndexes={
            isDivided ? [6, 6 + fertilizersData.length] : undefined
          }
          headCellClassName='bg-primary-light'
          headContainerClassName='bg-primary-light'
          tableWrapperClassName='rounded-xl whitespace-normal text-center mt-16'
          rowContainerClassName='hover:bg-primary-8 even:bg-neutralVariant-99'
          selection={false}
          columns={jobInstancesTableColumns(t, language, fertilizersData)}
          data={jobInstancesData ?? []}
          pagination={true}
          currentPage={tablePageNumber}
          perPage={PER_PAGE}
          totalCount={data?.total || 0}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
};

export default FertilizationTable;
